import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { aboutStats, aboutValues, contactItems, teamMembers } from "@/mocks/site";

const FORM_ADDR = "https://readdy.ai/api/form/db74hcgd6mcdj80oo7m0";

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useDocumentMeta({
    title: "关于我们-妖精漫画",
    description:
      "妖精漫画是一个专注妖精题材漫画作品与资讯的内容平台，了解我们的定位、团队与联系方式，欢迎通过反馈表单联系我们。",
    keywords: "妖精漫画,关于我们,妖精题材漫画平台,用户反馈,联系方式",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isOk = response.ok && parsed?.code === "OK" && !String(serverMsg).toLowerCase().includes("spam");

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl text-foreground-950 md:text-4xl">关于妖精漫画</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们相信，每一个奇幻故事里，都藏着一点现实世界的温柔。妖精漫画，想成为你发现这些故事的入口。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        {/* 定位 + 数据 */}
        <section className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">网站定位</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-600 md:text-base">
              妖精漫画是一个专注妖精题材漫画作品与资讯的内容平台，目标用户是喜欢妖精、奇幻元素的年轻读者与二次元爱好者。
              我们希望把分散的妖精题材作品整理到一起，让读者能够通过分类、筛选与搜索，快速找到感兴趣的内容，
              也能同步掌握最新作品动态与行业资讯。
            </p>
            <p className="mt-4 text-sm leading-loose text-foreground-600 md:text-base">
              我们不追求收录最多，只想认真做好垂直领域的筛选与推荐，让每一次点击都值得。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {aboutStats.map((stat) => (
              <div
                key={stat.label}
                className="flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`${stat.icon} text-xl`}></i>
                </span>
                <span className="font-heading text-2xl text-foreground-950">{stat.value}</span>
                <span className="text-xs text-foreground-500">{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 价值观 */}
        <section className="mt-14">
          <h2 className="mb-6 font-heading text-2xl text-foreground-950 md:text-3xl">我们坚持的事</h2>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {aboutValues.map((value) => (
              <div
                key={value.title}
                className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={`${value.icon} text-2xl`}></i>
                </span>
                <h3 className="text-base font-medium text-foreground-950">{value.title}</h3>
                <p className="text-sm leading-relaxed text-foreground-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 团队 + 联系方式 */}
        <section className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <h2 className="mb-6 font-heading text-2xl text-foreground-950 md:text-3xl">我们的团队</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {teamMembers.map((m) => (
                <div
                  key={m.name}
                  className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className={`${m.icon} text-xl`}></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{m.name}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="mb-6 font-heading text-2xl text-foreground-950 md:text-3xl">联系我们</h2>
            <div className="flex flex-col gap-4">
              {contactItems.map((c) => (
                <div
                  key={c.label}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${c.icon} text-xl`}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="mt-1 text-sm text-foreground-900">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 反馈表单 */}
        <section className="mt-14 rounded-lg border border-background-200 bg-secondary-50 p-6 md:p-8">
          <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">用户反馈</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            有想看却找不到的妖精漫画，或者对网站有任何建议，都欢迎留言告诉我们。
          </p>

          {status === "success" ? (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-lg border border-secondary-200 bg-background-50 py-12 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-checkbox-circle-line text-3xl"></i>
              </span>
              <p className="text-base text-foreground-900">感谢你的反馈！</p>
              <p className="text-sm text-foreground-500">我们已经收到你的留言，会认真阅读每一条建议。</p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-2 rounded-md bg-secondary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-secondary-600 cursor-pointer whitespace-nowrap"
              >
                再写一条
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="yjmh-feedback-form"
              onSubmit={handleSubmit}
              className="mt-6 flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label htmlFor="fb-name" className="text-sm text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你？"
                    className="h-11 rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="fb-email" className="text-sm text-foreground-800">
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="方便我们回复你"
                    className="h-11 rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="fb-topic" className="text-sm text-foreground-800">
                  反馈类型
                </label>
                <select
                  id="fb-topic"
                  name="topic"
                  className="h-11 rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 cursor-pointer"
                >
                  <option value="作品推荐">作品推荐</option>
                  <option value="功能建议">功能建议</option>
                  <option value="内容纠错">内容纠错</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="fb-message" className="text-sm text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="最多 500 字，期待听到你的想法"
                  className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
              </div>

              <input
                className="form-hp-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "error" && (
                <p className="flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex items-center gap-2 rounded-md bg-primary-500 px-8 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
                <span className="text-xs text-foreground-500">我们会在 3 个工作日内查看你的留言。</span>
              </div>
            </form>
          )}
        </section>

        <div className="mt-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}